import { useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { logError } from '@zg-rentals/log-error';
import { AppContext } from '../contexts/AppContextProvider';

/** Resolves a full URL to a Next.js-compatible href object for client-side navigation when the URL points to an in-app route. */
export default function useInternalHref(url: string | undefined) {
  const { paths } = useContext(AppContext);
  const router = useRouter();

  const internalHref = useMemo(() => {
    if (typeof window === 'undefined') return undefined;
    if (!url) return undefined;
    try {
      const matchesInternal = [paths?.inboxPage, paths?.leadManagementPage]
        .filter(Boolean)
        .some((p) => url.includes(p));
      if (!matchesInternal) return undefined;
      const { pathname, search } = new URL(url, window.location.origin);
      const appPath = pathname.slice(router.basePath.length);
      return {
        pathname: appPath,
        query: Object.fromEntries(new URLSearchParams(search)),
      };
    } catch (error) {
      logError({ error, context: { details: 'useInternalHref - failed to parse system message URL', url } });
      return undefined;
    }
  }, [url, paths?.inboxPage, paths?.leadManagementPage, router.basePath]);

  return { internalHref };
}
